import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XCircle,
  RefreshCw,
  Clock,
  Info,
  Play,
  Settings as SettingsIcon,
} from '@/utils/icons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { FeatureGate } from '@/components/common/FeatureGate';
import { ApiKeysManager, type ApiKeysManagerApi } from '@/components/dashboard/ApiKeysManager';
import { useAuth } from '@/hooks/useAuth';
import { usePollingStatus } from '@/hooks/usePollingStatus';
import { useFleetSummary } from '@/hooks/useMetrics';
import { formatDateTime } from '@/utils/formatters';
import { POLLING_INTERVALS } from '@/utils/constants';
import {
  getDemoAvailable,
  demoLogin,
  purgeData,
  getExportUrl,
  createOrgApiKey,
  listOrgApiKeys,
  revokeOrgApiKey,
  listOrgWebhooks,
  createOrgWebhook,
  deleteOrgWebhook,
  testOrgWebhook,
} from '@/services/api';
import { Download } from '@/utils/icons';

// Org-scoped endpoint adapter — the server binds every key to the session's
// own organization; these calls never touch the developer (all-orgs) surface.
const integrationsApi: ApiKeysManagerApi = {
  createKey: createOrgApiKey,
  listKeys: listOrgApiKeys,
  revokeKey: revokeOrgApiKey,
  listWebhooks: listOrgWebhooks,
  createWebhook: createOrgWebhook,
  deleteWebhook: deleteOrgWebhook,
  testWebhook: testOrgWebhook,
};

export function Settings() {
  const { session } = useAuth();
  const { config, loading: configLoading, updateConfig, trigger } = usePollingStatus();
  const { summary: fleetSummary } = useFleetSummary();
  const navigate = useNavigate();
  const isDemo = session?.isDemo === true;

  // Compliance target options: channel versions plus every version actually
  // running in the fleet — the fleet may run versions outside the channels.
  const versionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const v of [
      config?.upgradeChannelStable,
      config?.upgradeChannelBeta,
      config?.upgradeChannelAlpha,
      config?.targetVersion,
      ...Object.keys(fleetSummary?.versionDistribution ?? {}),
    ]) {
      if (v && v !== 'Unknown') set.add(v);
    }
    const parts = (s: string) => s.split('.').map((p) => parseInt(p, 10) || 0);
    return [...set].sort((a, b) => {
      const pa = parts(a);
      const pb = parts(b);
      const len = Math.max(pa.length, pb.length);
      for (let i = 0; i < len; i++) {
        const d = (pb[i] ?? 0) - (pa[i] ?? 0);
        if (d !== 0) return d;
      }
      return 0;
    });
  }, [
    config?.upgradeChannelStable,
    config?.upgradeChannelBeta,
    config?.upgradeChannelAlpha,
    config?.targetVersion,
    fleetSummary,
  ]);
  const [demoAvailable, setDemoAvailable] = useState(false);
  const [enteringDemo, setEnteringDemo] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    if (!isDemo) {
      getDemoAvailable()
        .then((res: { available: boolean }) => setDemoAvailable(res.available))
        .catch(() => setDemoAvailable(false));
    }
  }, [isDemo]);

  const [triggering, setTriggering] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Polling date overrides
  const [editingDates, setEditingDates] = useState(false);
  const [fromDateInput, setFromDateInput] = useState('');
  const [toDateInput, setToDateInput] = useState('');

  const handleTriggerPoll = async () => {
    setTriggering(true);
    try {
      await trigger();
      setTestResult({ success: true, message: 'Poll triggered successfully' });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to trigger poll',
      });
    } finally {
      setTriggering(false);
    }
  };

  const handleUpdateInterval = async (value: string) => {
    try {
      await updateConfig({ intervalSeconds: parseInt(value, 10) });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update polling interval',
      });
    }
  };

  const handleTogglePolling = async () => {
    if (!config) return;
    try {
      await updateConfig({ isActive: !config.isActive });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to toggle polling',
      });
    }
  };

  const handleStartEditDates = () => {
    if (!config) return;
    // Pre-fill inputs with current custom values, or empty for defaults
    setFromDateInput(
      config.pollingFromDate
        ? new Date(config.pollingFromDate).toISOString().slice(0, 16)
        : config.effectiveFromDate
          ? new Date(config.effectiveFromDate).toISOString().slice(0, 16)
          : ''
    );
    setToDateInput(
      config.pollingToDate ? new Date(config.pollingToDate).toISOString().slice(0, 16) : ''
    );
    setEditingDates(true);
  };

  const handleSaveDates = async () => {
    try {
      await updateConfig({
        pollingFromDate: fromDateInput ? new Date(fromDateInput).toISOString() : null,
        pollingToDate: toDateInput ? new Date(toDateInput).toISOString() : null,
      });
      setEditingDates(false);
      setTestResult({ success: true, message: 'Polling time range updated' });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update time range',
      });
    }
  };

  const handleResetDates = async () => {
    try {
      await updateConfig({
        pollingFromDate: null,
        pollingToDate: null,
      });
      setEditingDates(false);
      setTestResult({ success: true, message: 'Polling time range reset to defaults' });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to reset time range',
      });
    }
  };

  const formatDateDisplay = (dateStr: string | null, fallback: string) => {
    if (!dateStr) return fallback;
    if (dateStr === 'now') return 'Current time (at each poll)';
    return formatDateTime(dateStr);
  };

  return (
    <div className="dls-space-y-6 dls-max-w-2xl">
      <div>
        <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><SettingsIcon className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Settings</h1>
        <p className="dls-text-muted-foreground">
          Configure your Neeve connection and polling settings
        </p>
      </div>

      {isDemo && (
        <Alert>
          <Info className="dls-h-4 dls-w-4" />
          <AlertTitle>Demo Mode</AlertTitle>
          <AlertDescription>
            Settings are read-only in demo mode. Simulated polling is active — use "Poll Now" to
            trigger a simulation cycle.
          </AlertDescription>
        </Alert>
      )}

      {testResult && (
        <Alert variant={testResult.success ? 'default' : 'destructive'}>
          {testResult.success ? (
            <Info className="dls-h-4 dls-w-4" />
          ) : (
            <XCircle className="dls-h-4 dls-w-4" />
          )}
          <AlertDescription>{testResult.message}</AlertDescription>
        </Alert>
      )}

      {/* Polling configuration */}
      {session?.authenticated && (
        <Card>
          <CardHeader>
            <CardTitle>Polling Configuration</CardTitle>
            <CardDescription>
              Configure how often the dashboard fetches data from Neeve
            </CardDescription>
          </CardHeader>
          <CardContent className="dls-space-y-4">
            {configLoading ? (
              <PageLoader />
            ) : config ? (
              <>
                <div className="dls-flex dls-items-center dls-justify-between">
                  <div>
                    <p className="dls-font-medium">Polling Status</p>
                    <p className="dls-text-sm dls-text-muted-foreground">
                      {config.isActive ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <Button variant="secondary" onClick={handleTogglePolling} disabled={isDemo}>
                    {config.isActive ? 'Disable' : 'Enable'}
                  </Button>
                </div>

                <div className="dls-space-y-2">
                  <label className="dls-text-sm dls-font-medium">Polling Interval</label>
                  <Select
                    value={config.intervalSeconds.toString()}
                    onValueChange={handleUpdateInterval}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POLLING_INTERVALS.map((interval) => (
                        <SelectItem key={interval.value} value={interval.value.toString()}>
                          {interval.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* API Time Range */}
                <div className="dls-pt-4 dls-border-t dls-space-y-3">
                  <div className="dls-flex dls-items-center dls-gap-2">
                    <Clock className="dls-h-4 dls-w-4 dls-text-muted-foreground" />
                    <p className="dls-font-medium">API Query Time Range</p>
                    <InfoTooltip text="The from_updated_at and to_updated_at parameters sent to the Neeve API when fetching Node data. By default, 'From' uses the organization creation date and 'To' uses the current time at each poll." />
                  </div>

                  {editingDates ? (
                    <div className="dls-space-y-3 dls-bg-muted/50 dls-rounded-lg dls-p-4">
                      <div className="dls-space-y-2">
                        <label className="dls-text-sm dls-font-medium">From (from_updated_at)</label>
                        <Input
                          type="datetime-local"
                          value={fromDateInput}
                          onChange={(e) => setFromDateInput(e.target.value)}
                        />
                        <p className="dls-text-xs dls-text-muted-foreground">
                          Leave empty to use organization creation date
                          {config.orgCreatedAt && ` (${formatDateTime(config.orgCreatedAt)})`}
                        </p>
                      </div>
                      <div className="dls-space-y-2">
                        <label className="dls-text-sm dls-font-medium">To (to_updated_at)</label>
                        <Input
                          type="datetime-local"
                          value={toDateInput}
                          onChange={(e) => setToDateInput(e.target.value)}
                        />
                        <p className="dls-text-xs dls-text-muted-foreground">
                          Leave empty to use current time at each poll
                        </p>
                      </div>
                      <div className="dls-flex dls-gap-2">
                        <Button size="sm" onClick={handleSaveDates}>
                          Save
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditingDates(false)}>
                          Cancel
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleResetDates}>
                          Reset to Defaults
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="dls-space-y-2">
                      <div className="dls-grid dls-grid-cols-2 dls-gap-4 dls-text-sm">
                        <div>
                          <p className="dls-text-muted-foreground">From (from_updated_at)</p>
                          <p className="dls-font-mono dls-text-xs dls-mt-0.5">
                            {formatDateDisplay(config.effectiveFromDate, 'Not set')}
                          </p>
                          {config.pollingFromDate && (
                            <p className="dls-text-xs dls-text-neeve-accent-blue dls-mt-0.5">Custom override</p>
                          )}
                          {!config.pollingFromDate && config.orgCreatedAt && (
                            <p className="dls-text-xs dls-text-muted-foreground dls-mt-0.5">
                              From org creation date
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="dls-text-muted-foreground">To (to_updated_at)</p>
                          <p className="dls-font-mono dls-text-xs dls-mt-0.5">
                            {formatDateDisplay(config.effectiveToDate, 'Current time')}
                          </p>
                          {config.pollingToDate && (
                            <p className="dls-text-xs dls-text-neeve-accent-blue dls-mt-0.5">Custom override</p>
                          )}
                          {!config.pollingToDate && (
                            <p className="dls-text-xs dls-text-muted-foreground dls-mt-0.5">
                              Dynamic (current time)
                            </p>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="secondary" onClick={handleStartEditDates}>
                        Modify Time Range
                      </Button>
                    </div>
                  )}
                </div>

                <div className="dls-pt-4 dls-border-t">
                  <div className="dls-flex dls-items-center dls-justify-between dls-mb-4">
                    <div>
                      <p className="dls-font-medium">Last Successful Poll</p>
                      <p className="dls-text-sm dls-text-muted-foreground">
                        {config.lastSuccessfulPoll
                          ? formatDateTime(config.lastSuccessfulPoll)
                          : 'Never'}
                      </p>
                    </div>
                    <Button variant="secondary" onClick={handleTriggerPoll} disabled={triggering}>
                      <RefreshCw className={`dls-h-4 dls-w-4 dls-mr-2 ${triggering ? 'dls-animate-spin' : ''}`} />
                      {triggering ? 'Polling...' : 'Poll Now'}
                    </Button>
                  </div>

                  {config.lastPollError && (
                    <Alert variant="destructive">
                      <XCircle className="dls-h-4 dls-w-4" />
                      <AlertTitle>Last Poll Error</AlertTitle>
                      <AlertDescription>{config.lastPollError}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Current connection info */}
      {session?.tenant && (
        <Card>
          <CardHeader>
            <CardTitle>Current Connection</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="dls-space-y-2">
              <div className="dls-flex dls-justify-between">
                <dt className="dls-text-muted-foreground">Organization</dt>
                <dd className="dls-font-medium">{session.tenant.name}</dd>
              </div>
              <div className="dls-flex dls-justify-between">
                <dt className="dls-text-muted-foreground">Portal URL</dt>
                <dd className="dls-font-mono dls-text-sm">{session.tenant.portalUrl}</dd>
              </div>
              {config && (
                <div className="dls-flex dls-justify-between">
                  <dt className="dls-text-muted-foreground">Connection Status</dt>
                  <dd className="dls-flex dls-items-center dls-gap-2">
                    {config.lastPollError ? (
                      <>
                        <span className="dls-h-2 dls-w-2 dls-rounded-full dls-bg-status-offline" />
                        <span className="dls-text-sm dls-text-status-offline">Error</span>
                      </>
                    ) : config.lastSuccessfulPoll ? (
                      <>
                        <span className="dls-h-2 dls-w-2 dls-rounded-full dls-bg-status-online" />
                        <span className="dls-text-sm dls-text-status-online">Healthy</span>
                      </>
                    ) : (
                      <>
                        <span className="dls-h-2 dls-w-2 dls-rounded-full dls-bg-muted-foreground" />
                        <span className="dls-text-sm dls-text-muted-foreground">No polls yet</span>
                      </>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Resource & Version Settings */}
      {session?.authenticated && config && (
        <Card>
          <CardHeader>
            <CardTitle>Monitoring Thresholds</CardTitle>
            <CardDescription>
              Configure resource utilization alert thresholds and version compliance target
            </CardDescription>
          </CardHeader>
          <CardContent className="dls-space-y-6">
            {/* Resource Thresholds */}
            <div className="dls-space-y-3">
              <div className="dls-flex dls-items-center dls-gap-2">
                <p className="dls-font-medium">Resource Alert Thresholds</p>
                <InfoTooltip text="CPU, memory, and disk utilization above these thresholds will be flagged in Inventory. Caution = warning, Critical = urgent." />
              </div>
              <div className="dls-grid dls-grid-cols-2 dls-gap-4">
                <div className="dls-space-y-2">
                  <label className="dls-text-sm dls-text-muted-foreground">Caution (%)</label>
                  <Select
                    value={config.resourceCautionThreshold.toString()}
                    onValueChange={async (v) => {
                      try {
                        await updateConfig({ resourceCautionThreshold: parseInt(v, 10) });
                      } catch {
                        setTestResult({ success: false, message: 'Failed to update caution threshold' });
                      }
                    }}
                    disabled={isDemo}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[50, 60, 70, 75, 80].map((v) => (
                        <SelectItem key={v} value={v.toString()}>{v}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="dls-space-y-2">
                  <label className="dls-text-sm dls-text-muted-foreground">Critical (%)</label>
                  <Select
                    value={config.resourceCriticalThreshold.toString()}
                    onValueChange={async (v) => {
                      try {
                        await updateConfig({ resourceCriticalThreshold: parseInt(v, 10) });
                      } catch {
                        setTestResult({ success: false, message: 'Failed to update critical threshold' });
                      }
                    }}
                    disabled={isDemo}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[80, 85, 90, 95].map((v) => (
                        <SelectItem key={v} value={v.toString()}>{v}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Version Target */}
            {(config.upgradeChannelStable || config.upgradeChannelBeta || config.upgradeChannelAlpha) && (
              <div className="dls-pt-4 dls-border-t dls-space-y-3">
                <div className="dls-flex dls-items-center dls-gap-2">
                  <p className="dls-font-medium">Version Compliance Target</p>
                  <InfoTooltip text="Set the target version for compliance checking. Nodes not on this version will be flagged as outdated. Leave on 'Auto-detect' to use fleet majority logic." />
                </div>
                <div className="dls-text-sm dls-text-muted-foreground dls-space-y-1 dls-mb-2">
                  <p>Available channel versions:</p>
                  <div className="dls-flex dls-gap-4 dls-text-xs dls-font-mono">
                    {config.upgradeChannelStable && <span>STABLE: {config.upgradeChannelStable}</span>}
                    {config.upgradeChannelBeta && <span>BETA: {config.upgradeChannelBeta}</span>}
                    {config.upgradeChannelAlpha && <span>ALPHA: {config.upgradeChannelAlpha}</span>}
                  </div>
                </div>
                <Select
                  value={config.targetVersion ?? '_auto'}
                  onValueChange={async (v) => {
                    try {
                      await updateConfig({ targetVersion: v === '_auto' ? null : v });
                    } catch {
                      setTestResult({ success: false, message: 'Failed to update target version' });
                    }
                  }}
                  disabled={isDemo}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_auto">Auto-detect (fleet majority)</SelectItem>
                    {versionOptions.map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* 2FA Org Policy Info (read-only) */}
            {config.twoFactorEnabled !== null && (
              <div className="dls-pt-4 dls-border-t dls-space-y-2">
                <p className="dls-font-medium">Organization 2FA Policy</p>
                <div className="dls-text-sm">
                  <span className={config.twoFactorForced ? 'dls-text-status-online' : 'dls-text-muted-foreground'}>
                    {config.twoFactorForced
                      ? '2FA is enforced for all users by organization policy'
                      : config.twoFactorEnabled
                        ? '2FA is enabled but not enforced'
                        : '2FA is not enabled'}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data export & management */}
      {session?.authenticated && !isDemo && (
        <Card>
          <CardHeader>
            <CardTitle>Data Management</CardTitle>
            <CardDescription>
              Export or purge all collected data for your organization
            </CardDescription>
          </CardHeader>
          <CardContent className="dls-space-y-4">
            <div>
              <p className="dls-text-sm dls-font-medium dls-mb-2">Export Data</p>
              <Button
                variant="secondary"
                onClick={() => {
                  window.open(getExportUrl(), '_blank');
                }}
              >
                <Download className="dls-h-4 dls-w-4 dls-mr-2" />
                Export Full JSON
              </Button>
              <p className="dls-text-xs dls-text-muted-foreground dls-mt-1">
                Download all nodes, networks, services, and historical data as JSON
              </p>
            </div>
            <div className="dls-pt-4 dls-border-t">
              <p className="dls-text-sm dls-font-medium dls-mb-2">Purge Data</p>
              <p className="dls-text-xs dls-text-muted-foreground dls-mb-2">
                Remove all collected data (nodes, services, networks, history). Connection settings are preserved.
              </p>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!confirm('Are you sure? This will delete all collected data and cannot be undone.')) return;
                setPurging(true);
                try {
                  const result = await purgeData();
                  const totalPurged = Object.values(result.purged).reduce((a, b) => a + b, 0);
                  setTestResult({
                    success: true,
                    message: `Purged ${totalPurged} records successfully`,
                  });
                } catch {
                  setTestResult({ success: false, message: 'Failed to purge data' });
                } finally {
                  setPurging(false);
                }
              }}
              disabled={purging}
            >
              {purging ? 'Purging...' : 'Purge All Data'}
            </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* API & Webhooks (org-scoped self-service, flag-gated) */}
      {session?.authenticated && !isDemo && (
        <FeatureGate feature="capability.api-integrations">
          <ApiKeysManager
            api={integrationsApi}
            title="API & Webhooks"
            description="Create API keys and webhooks for your organization. Keys only access your organization's data."
          />
        </FeatureGate>
      )}

      {/* Demo mode */}
      {!isDemo && demoAvailable && (
        <Card>
          <CardHeader>
            <CardTitle>Demo Mode</CardTitle>
            <CardDescription>
              Explore Neeve Insights with sample data from a fictional organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="secondary"
              onClick={async () => {
                setEnteringDemo(true);
                try {
                  await demoLogin();
                  navigate('/');
                } catch {
                  setEnteringDemo(false);
                }
              }}
              disabled={enteringDemo}
            >
              <Play className="dls-h-4 dls-w-4 dls-mr-2" />
              {enteringDemo ? 'Entering Demo...' : 'Enter Demo Mode'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
